import React, { lazy, Suspense, useMemo } from "react";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { useChartTheme } from "../../theme";
import type { FailureMode, FailurePoint } from "../types";
import { buildScatterFigure } from "../utils/scatterData";

// Lazy boundary — Plotly (~3 MB) loads only when this page mounts (design doc
// 015 §6). `PlotlyPlot` is the only module that imports Plotly statically.
const Plot = lazy(() => import("./PlotlyPlot"));

interface ClusterScatterProps {
  points: FailurePoint[];
  modes: FailureMode[];
  selectedModeId: string | null;
  onSelectMode: (id: string) => void;
  /** Session-stable palette colour for a mode id (see `useModeColors`). */
  colorForMode: (id: string) => string;
  /** Draw the grey unassigned long tail. Off by default cuts visual clutter. */
  showUnassigned: boolean;
}

// Shape of the click payload we read from Plotly — each clicked point carries
// the `customdata` we attached (its failure_mode_id). Unassigned points have no
// customdata, so a click on the grey long tail selects nothing.
interface PlotClickEvent {
  points?: Array<{ customdata?: string }>;
}

/**
 * The cluster map: a Plotly scatter coloured by failure mode with per-mode
 * convex-hull fills and a grey unassigned long tail (design doc 015 §6).
 * Clicking a point selects its mode; the legend drives the same selection.
 */
export function ClusterScatter({
  points,
  modes,
  selectedModeId,
  onSelectMode,
  colorForMode,
  showUnassigned,
}: ClusterScatterProps) {
  const chart = useChartTheme();
  const { data, layout } = useMemo(
    () =>
      buildScatterFigure(
        points,
        modes,
        selectedModeId,
        colorForMode,
        showUnassigned,
        chart.surface,
      ),
    [points, modes, selectedModeId, colorForMode, showUnassigned, chart.surface],
  );

  const handleClick = (event: PlotClickEvent) => {
    const id = event?.points?.[0]?.customdata;
    if (id) onSelectMode(id);
  };

  return (
    <Suspense fallback={<ChartSkeleton showTitle={false} height="100%" />}>
      <Plot
        data={data}
        layout={layout}
        config={{
          displaylogo: false,
          responsive: true,
          displayModeBar: false,
          scrollZoom: true,
        }}
        style={{ width: "100%", height: "100%" }}
        useResizeHandler
        onClick={handleClick}
      />
    </Suspense>
  );
}
